import { Box, Button, Grid, Modal, TextField, Typography } from "@mui/material";
import * as React from "react";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { fetchData } from "../common/services/ApiClient";
import { ApiBaseUrl } from "../config";
import {
  KeyValue,
  SimpleTableRow,
  TableHeader,
  SortDirection,
  Query,
  BaseTableCell,
} from "../interfaces";
import ResultPagination from "./ResultPagination";
import usePagination from "../hooks/usePagination";
import MultiHeaderTable from "./MultiHeaderTable/MultiHeaderTable";
import SimpleTableBody from "./MultiHeaderTable/SimpleTableBody";
import useSortProperties from "../hooks/useSortProperties";

export const LoadReportModal = ({
  showLoadReportModal,
  currentApp,
  onClose,
  onSubmit,
  appState,
  updateSort,
}: {
  showLoadReportModal: boolean;
  currentApp: string;
  onClose: (generateReport?: boolean) => void;
  onSubmit: (data: any) => void;
  appState: Query;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
}) => {
  const { xs, sm } = useBreakpoint();
  const isMobile = xs || sm;
  const [input, setInput] = React.useState("");
  const [showReportVariablePreview, setShowReportVariablePreview] =
    React.useState<boolean>(false);

  const loadReportModalStyle = {
    position: "absolute",
    top: isMobile ? "100%" : "5%",
    left: "50%",
    transform: isMobile ? "translate(-50%, -100%)" : "translateX(-50%)",
    width: isMobile ? "100%" : 800,
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    pt: 4,
    px: isMobile ? 2 : 4,
    pb: showReportVariablePreview ? 2 : 6,
    textAlign: "center",
    borderRadius: isMobile ? "16px 16px 0 0" : 2,
    outline: "none",
  };

  const [structurePreview, setStructurePreview] = React.useState<
    SimpleTableRow[] | null
  >();
  const {
    page,
    rowsPerPage,
    handleChangePage,
    handlePageJump,
    handleChangeRowsPerPage,
  } = usePagination(1, 25, structurePreview?.length);

  const apiFieldToObjectKey: Record<string, string> = {
    lineDesc: "label",
    incomeBasis: "incomebasis",
    unitType: "unittype",
    var: "variable",
  };

  const sortFunction = (a: SimpleTableRow, b: SimpleTableRow) => {
    const objectKey = apiFieldToObjectKey[appState.sort.sortField];

    if (!objectKey) return 0;

    const aVal = (a[objectKey] as BaseTableCell)?.value ?? "";
    const bVal = (b[objectKey] as BaseTableCell)?.value ?? "";

    const comparison = String(aVal).localeCompare(String(bVal));

    return appState.sort.sortOrder === "ASC" ? comparison : -comparison;
  };

  const visibleVars = structurePreview
    ?.sort(sortFunction)
    .slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const previewTableHeaders: TableHeader[] = [
    {
      title: "Line Item Description",
      colSpan: 1,
      styles: { textAlign: "left", fontWeight: "bold" },
      apiField: "lineDesc",
    },
    {
      title: "Income Basis",
      colSpan: 1,
      styles: { fontWeight: "bold" },
      apiField: "incomeBasis",
    },
    {
      title: "Unit Type",
      colSpan: 1,
      styles: { fontWeight: "bold" },
      apiField: "unitType",
    },
    {
      title: "Variable",
      colSpan: 1,
      styles: { fontWeight: "bold", textAlign: "right" },
      apiField: "var",
    },
  ];

  const handleGenerate = () => {
    onSubmit(reportStructure);
    onClose(true);
    setInput("");
  };
  const [reportStructure, setReportStructure] = React.useState<
    KeyValue[] | null
  >(null);
  const handleViewPreview = () => {
    if (reportStructure && reportStructure[0].fields) {
      const tableRows: SimpleTableRow[] = reportStructure[0].fields
        .filter(
          (vari: any) =>
            vari.variable_ytd !== "NAMEFULL" && vari.variable_ytd !== "TOTAL",
        )
        .map((vari: any) => ({
          label: { value: vari.label, styles: { textAlign: "left" } },
          incomebasis: { value: vari.label.includes("YTD") ? "YTD" : "Qtly" },
          unittype: { value: vari.unitType },
          variable: {
            value: vari.variable_ytd,
            styles: { textAlign: "right" },
          },
        }));
      setStructurePreview(tableRows);
      setShowReportVariablePreview(true);
    }
  };

  const isValid = input.trim().length >= 1;

  const [invalidInput, setInvalidInput] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string>("");

  React.useEffect(() => {
    let isMounted = true;
    if (isMounted && isValid) {
      const key = input.trim();
      const isValidKey = /^[a-z0-9]+-[a-z0-9]+$/i;
      if (!isValidKey.test(key)) {
        setInvalidInput(true);
        return;
      }
      (async () => {
        await fetchData(`${ApiBaseUrl()}/shared_reports/${key}`)
          .then((response: any) => {
            const reportStructure = response?.body?.structure;
            const includesDemographicInfo = reportStructure?.[0]?.fields?.some(
              (val: KeyValue) =>
                val?.demographicOnly && val.demographicOnly === "TRUE",
            );
            if (includesDemographicInfo && currentApp === "peerGroup") {
              setInvalidInput(true);
              setErrorMessage(
                "Demographic variables detected. Structure-related variables are not applicable to peer groups.",
              );
              return null;
            }
            if (reportStructure?.length && key !== "") {
              setReportStructure(reportStructure);
              setInvalidInput(false);
              setReportStructure(reportStructure);
            } else {
              setInvalidInput(true);
            }
          })
          .catch((error) => {
            setInvalidInput(true);
            console.error(`${error.name} : ${error.message}`);
          });
      })();
    }
  }, [currentApp, input, isValid, onClose, onSubmit]);

  const sortHandler = useSortProperties(appState, updateSort);

  const reportVariablePreview = (
    <>
      <Typography
        id="modal-title"
        color="primary.main"
        variant={isMobile ? "h5" : "h2"}
        sx={{ fontWeight: "bold" }}
      >
        Report Variable Preview
      </Typography>
      <Typography
        id="modal-description"
        sx={{
          mt: 2,
          textAlign: "left",
        }}
        color="primary.main"
        variant="body2"
      >
        These are the variables that make up the structure of this selected
        customize report.
      </Typography>

      {structurePreview && visibleVars && (
        <ResultPagination
          count={structurePreview.length}
          handleChangePage={handleChangePage as any}
          handlePickPage={handlePageJump}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          hideBottomPagination
          limit={rowsPerPage}
          pageNumber={page}
        >
          <Box
            sx={{
              minHeight: "30vh",
              maxHeight: "55vh",
              mt: 1,
              overflowY: "scroll",
            }}
          >
            <MultiHeaderTable
              tableHeaders={[previewTableHeaders]}
              tableBody={<SimpleTableBody tableData={visibleVars} />}
              sortProperties={sortHandler}
            ></MultiHeaderTable>
          </Box>
        </ResultPagination>
      )}

      <Grid
        sx={{
          mt: 3,
          display: "flex",
          gap: 2,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          id="cancel"
          onClick={() => setShowReportVariablePreview(false)}
          variant="contained"
          color="secondary"
          sx={{ flex: 1, maxWidth: 160, maxHeight: 40 }}
        >
          Cancel
        </Button>

        <Button
          id="generate-report"
          onClick={handleGenerate}
          variant="contained"
          color="secondary"
          disabled={!input.trim() || invalidInput}
          sx={{ flex: 1, maxWidth: 160, maxHeight: 40 }}
        >
          {currentApp === "savedCustomContent"
            ? "Save Shared Report"
            : "Generate Report"}
        </Button>
      </Grid>
    </>
  );

  const shareReport = (
    <>
      <Typography
        id="modal-title"
        color="primary.main"
        variant={isMobile ? "h5" : "h2"}
        sx={{ fontWeight: "bold" }}
      >
        Load a Shared Report Code
      </Typography>

      <Typography
        id="modal-description"
        sx={{
          mt: 2,
          textAlign: "left",
        }}
        color="primary.main"
        variant="body2"
      >
        This interface allows you to load a previously created report structure
        (the selected variables) that was shared with you. Copy the provided
        code and paste it into the form field below.
      </Typography>
      <Typography
        sx={{
          textAlign: "left",
        }}
        color="primary.main"
        variant="body2"
      >
        You can run shared custom reports directly, however you can also save
        customized reports for future use directly in the Financial Reporting
        interface. We are using Login.gov to secure customized content. You can
        create a new account or link an existing Login.gov account to save your
        customized content. Login.gov is an official product of the GSA that
        partners with other Federal agencies as well as State and Local
        entities. Please see the <b>Personalized Account Access</b> banner on
        the previous page for more information.
      </Typography>
      <Typography
        display="block"
        sx={{
          textAlign: "left",
          fontWeight: "bold",
          paddingBottom: 0,
        }}
        color="primary.main"
        variant="body2"
      >
        Enter your shared customized report code
      </Typography>
      <Typography
        display="block"
        sx={{
          textAlign: "left",
        }}
        color="primary.main"
        variant="body2"
      >
        Copy the shared link from the email you received and paste it into the
        area shown below.
      </Typography>
      <TextField
        fullWidth
        id="share-report-code"
        value={input}
        placeholder="Paste Custom Report Code"
        error={!!errorMessage || invalidInput}
        helperText={
          errorMessage ||
          (invalidInput
            ? "Invalid code. Please re-copy and paste the code again."
            : "")
        }
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleGenerate();
          }
        }}
        sx={{
          "& .MuiInputBase-root": {
            height: "40px",
            mt: 4,
          },
        }}
      />
      <Grid
        sx={{
          mt: 6,
          display: "flex",
          gap: 2,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          id="cancel"
          onClick={() => {
            onClose(false);
            setInput("");
          }}
          variant="contained"
          color="secondary"
          sx={{ flex: 1, maxWidth: 160, maxHeight: 40 }}
        >
          Cancel
        </Button>

        <Button
          id="preview-variables"
          onClick={() => handleViewPreview()}
          variant="contained"
          color="secondary"
          disabled={!input.trim() || invalidInput}
          sx={{
            flex: 1,
            maxWidth: 160,
            maxHeight: 40,
          }}
        >
          Preview Variables
        </Button>

        <Button
          id="generate-report"
          onClick={handleGenerate}
          variant="contained"
          color="secondary"
          disabled={!input.trim() || invalidInput}
          sx={{ flex: 1, maxWidth: 160, maxHeight: 40 }}
        >
          {currentApp === "savedCustomContent"
            ? "Save Shared Report"
            : "Generate Report"}
        </Button>
      </Grid>
    </>
  );

  return (
    <Modal
      id="load-report-modal"
      open={showLoadReportModal}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return; //disables clickaway
        onClose();
      }}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={loadReportModalStyle}>
        {showReportVariablePreview ? reportVariablePreview : shareReport}
      </Box>
    </Modal>
  );
};
